/**
 * `diagram/gantt.ts` — browser-side gantt parse/serialize (Gantt PRD v1b, 2026-07-08).
 *
 * Mirrors the Python engine (`backend/aletheia/diagrams/gantt.py`) for the interactive
 * `GanttCanvas`: the canvas parses a gantt fence into a `GanttSpec`, the user drags a bar (new
 * start / duration), and we re-serialize back to the fence — same round-trip contract the
 * flowchart canvas has. The TS↔Python parity is pinned by the shared fixture `gantt_contract.json`
 * so a gantt authored over MCP and one edited on the canvas stay interchangeable.
 */

export type GanttStatus = 'normal' | 'active' | 'done' | 'crit';

export interface GanttTask {
  id: string;
  title: string;
  start: string | null;
  after: string[];
  duration: string | null;
  end: string | null;
  status: GanttStatus;
  milestone: boolean;
  owner: string | null;
  /**
   * Free-text description. Mermaid gantt has no field for it, so it rides in the sidecar comment
   * (see below) rather than the task line — round-trips through the canvas, MCP, and chat.
   */
  description: string | null;
  /**
   * Arbitrary user/MCP metadata (the "any other useful data" fields) as an ordered key→value map.
   * Also sidecar-carried. Values are strings — the panel edits them as text.
   */
  fields: Record<string, string>;
}

export interface GanttSection {
  name: string;
  tasks: GanttTask[];
}

export interface GanttSpec {
  title: string;
  dateFormat: string;
  axisFormat: string | null;
  excludes: string | null;
  todayMarker: boolean;
  tickInterval: string | null;
  sections: GanttSection[];
}

const STATUS_TAGS = new Set(['crit', 'active', 'done']);
const DURATION_RE = /^\d+(?:\.\d+)?\s*[dhwy]$/i;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TASK_LINE_RE = /^(.+?)\s*:\s*(.+)$/;
const OWNER_SEP = ' · ';

/**
 * Sidecar comment carrying per-task metadata that mermaid gantt syntax has no field for
 * (`description` + freeform `fields`). It's a plain `%%` comment — mermaid drops it, every other
 * renderer drops it, and copy-paste carries it because it's just text. Mirrors the flowchart
 * `%% @aletheia` sidecar. JSON shape: `{"tasks":{"<id>":{"description":"…","fields":{…}}}}`.
 * Malformed / unknown → treated as absent (never throws, never loses the diagram).
 */
const GANTT_SIDECAR_RE = /^\s*%%\s*@aletheia:gantt\s+(\{.*\})\s*$/;

export function emptyGanttSpec(title = ''): GanttSpec {
  return {
    title,
    dateFormat: 'YYYY-MM-DD',
    axisFormat: null,
    excludes: null,
    todayMarker: true,
    tickInterval: null,
    sections: [],
  };
}

export function ganttTasks(spec: GanttSpec): GanttTask[] {
  return spec.sections.flatMap((s) => s.tasks);
}

/** True if the fence body's first meaningful line is a `gantt` header. */
export function isGantt(source: string): boolean {
  for (const raw of source.split('\n')) {
    const line = raw.trim();
    if (line === '' || line.startsWith('%%')) continue;
    return line.split(/\s+/)[0].toLowerCase() === 'gantt';
  }
  return false;
}

// ── serialize (spec → mermaid) ───────────────────────────────────────────────

function labelOf(task: GanttTask): string {
  return task.owner ? `${task.title}${OWNER_SEP}${task.owner}` : task.title;
}

function tailOf(task: GanttTask): string {
  const parts: string[] = [];
  if (task.milestone) parts.push('milestone');
  else if (STATUS_TAGS.has(task.status)) parts.push(task.status);
  parts.push(task.id);
  if (task.after.length > 0) parts.push('after ' + task.after.join(' '));
  else if (task.start) parts.push(task.start);
  if (task.milestone) parts.push('0d');
  else if (task.duration) parts.push(task.duration);
  else if (task.end) parts.push(task.end);
  return parts.join(', ');
}

/**
 * Build the sidecar JSON object for a spec, or `null` if no task carries metadata (so a plain gantt
 * stays byte-for-byte plain — the sidecar only appears once there's something to store).
 */
function sidecarPayload(spec: GanttSpec): { tasks: Record<string, unknown> } | null {
  const tasks: Record<string, unknown> = {};
  for (const t of ganttTasks(spec)) {
    const entry: { description?: string; fields?: Record<string, string> } = {};
    if (t.description) entry.description = t.description;
    if (t.fields && Object.keys(t.fields).length > 0) entry.fields = t.fields;
    if (Object.keys(entry).length > 0) tasks[t.id] = entry;
  }
  return Object.keys(tasks).length > 0 ? { tasks } : null;
}

/** Render a `GanttSpec` to mermaid gantt source (the fence body). */
export function serializeGantt(spec: GanttSpec): string {
  const lines = ['gantt', `    title ${spec.title}`, `    dateFormat ${spec.dateFormat}`];
  if (spec.axisFormat) lines.push(`    axisFormat ${spec.axisFormat}`);
  if (spec.excludes) lines.push(`    excludes ${spec.excludes}`);
  if (spec.tickInterval) lines.push(`    tickInterval ${spec.tickInterval}`);
  if (!spec.todayMarker) lines.push('    todayMarker off');
  for (const section of spec.sections) {
    lines.push(`    section ${section.name}`);
    for (const task of section.tasks) lines.push(`        ${labelOf(task)} :${tailOf(task)}`);
  }
  // Metadata sidecar last — a comment mermaid ignores, keyed by task id. JSON keys are sorted so
  // the output is stable (no spurious diffs when only in-memory field order differs).
  const payload = sidecarPayload(spec);
  if (payload) lines.push(`    %% @aletheia:gantt ${stableStringify(payload)}`);
  return lines.join('\n');
}

/** Deterministic JSON: object keys sorted recursively, so serialize is stable across runs. */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.keys(value as Record<string, unknown>)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value);
}

// ── parse (mermaid → spec) ───────────────────────────────────────────────────

function splitLabel(label: string): [string, string | null] {
  const idx = label.indexOf(OWNER_SEP);
  if (idx >= 0) {
    const owner = label.slice(idx + OWNER_SEP.length).trim();
    return [label.slice(0, idx).trim(), owner || null];
  }
  return [label.trim(), null];
}

function parseTask(label: string, tail: string): GanttTask {
  const tokens = tail
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t !== '');
  let milestone = false;
  let status: GanttStatus = 'normal';
  let id = '';
  let start: string | null = null;
  let after: string[] = [];
  let duration: string | null = null;
  let end: string | null = null;

  for (const tok of tokens) {
    const low = tok.toLowerCase();
    if (low === 'milestone') {
      milestone = true;
    } else if (STATUS_TAGS.has(low)) {
      status = low as GanttStatus;
    } else if (low.startsWith('after ')) {
      after = tok.slice('after '.length).split(/\s+/).filter(Boolean);
    } else if (ISO_DATE_RE.test(tok)) {
      if (start === null && duration === null && after.length === 0) start = tok;
      else end = tok;
    } else if (DURATION_RE.test(tok)) {
      duration = tok === '0d' && milestone ? null : tok;
    } else if (id === '' && /^[A-Za-z0-9_]+$/.test(tok)) {
      id = tok;
    }
    // anything else: ignore (never throw)
  }

  const [title, owner] = splitLabel(label);
  return {
    id: id || title.toLowerCase().replace(/\W+/g, '_').slice(0, 24) || 'task',
    title,
    start,
    after,
    duration,
    end,
    status,
    milestone,
    owner,
    description: null,
    fields: {},
  };
}

/**
 * Parse a `%% @aletheia:gantt {json}` sidecar line into `{ tasks: { id: {description, fields} } }`.
 * Returns an empty map on any malformation (bad JSON, wrong shape) — never throws.
 */
function parseSidecar(line: string): Record<string, { description: string | null; fields: Record<string, string> }> {
  const m = GANTT_SIDECAR_RE.exec(line);
  if (!m) return {};
  let json: unknown;
  try {
    json = JSON.parse(m[1]);
  } catch {
    return {};
  }
  const tasks = (json as { tasks?: unknown })?.tasks;
  if (!tasks || typeof tasks !== 'object') return {};
  const out: Record<string, { description: string | null; fields: Record<string, string> }> = {};
  for (const [id, raw] of Object.entries(tasks as Record<string, unknown>)) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as { description?: unknown; fields?: unknown };
    const fields: Record<string, string> = {};
    if (r.fields && typeof r.fields === 'object') {
      for (const [k, v] of Object.entries(r.fields as Record<string, unknown>)) {
        if (typeof v === 'string') fields[k] = v;
        else if (typeof v === 'number' || typeof v === 'boolean') fields[k] = String(v);
      }
    }
    out[id] = { description: typeof r.description === 'string' ? r.description : null, fields };
  }
  return out;
}

function opt(line: string, key: string): string | null {
  const m = new RegExp(`^${key}\\s+(.+)$`).exec(line);
  return m ? m[1].trim() : null;
}

/** Parse mermaid gantt source into a `GanttSpec`. Never throws; round-trips `serializeGantt`. */
export function parseGantt(source: string): GanttSpec {
  const spec = emptyGanttSpec();
  let current: GanttSection | null = null;
  let sidecar: Record<string, { description: string | null; fields: Record<string, string> }> = {};
  for (const raw of source.split('\n')) {
    const line = raw.trim();
    if (line.startsWith('%%')) {
      // A `%%` line is a comment to mermaid; ours carries the metadata sidecar (last one wins).
      const parsed = parseSidecar(line);
      if (Object.keys(parsed).length > 0) sidecar = parsed;
      continue;
    }
    if (line === '' || line.toLowerCase() === 'gantt') continue;
    let v: string | null;
    if ((v = opt(line, 'title')) !== null) spec.title = v;
    else if ((v = opt(line, 'dateFormat')) !== null) spec.dateFormat = v;
    else if ((v = opt(line, 'axisFormat')) !== null) spec.axisFormat = v;
    else if ((v = opt(line, 'excludes')) !== null) spec.excludes = v;
    else if ((v = opt(line, 'tickInterval')) !== null) spec.tickInterval = v;
    else if (/^todayMarker\s+off\b/.test(line)) spec.todayMarker = false;
    else if ((v = opt(line, 'section')) !== null) {
      current = { name: v, tasks: [] };
      spec.sections.push(current);
    } else {
      const m = TASK_LINE_RE.exec(line);
      if (m) {
        if (current === null) {
          current = { name: '', tasks: [] };
          spec.sections.push(current);
        }
        current.tasks.push(parseTask(m[1], m[2]));
      }
    }
  }
  // Fold the sidecar metadata back onto tasks by id (unknown ids are ignored — a task may have been
  // renamed/deleted since the sidecar was written; the orphan entry just drops out on next write).
  if (Object.keys(sidecar).length > 0) {
    for (const t of ganttTasks(spec)) {
      const meta = sidecar[t.id];
      if (meta) {
        t.description = meta.description;
        t.fields = meta.fields;
      }
    }
  }
  return spec;
}
